/**
 * @file document-view.ts
 * @description Как показать документ CRM в кабинете магазина (phase19.31,
 * зона «Клиенты и сделки», часть 3; SC26, SC43): тон статуса, деньги, дата,
 * контрагент, основание, позиции и пробелы в реквизитах продавца.
 *
 * Чистые функции без React — одни и те же в реестре, в карточке документа и
 * во вкладках «Документы» сделки и заказа.
 *
 * Всё берётся из готовых полей сервера (контракт §19): покупатель
 * (`counterpartyName/Inn`) — у любого статуса, сделка (`dealName`),
 * позиции (`items`), реквизиты продавца (`legalProfileIncomplete`,
 * `legalProfileGaps`). Кабинет снимок печати не разбирает и заготовку
 * реквизитов не угадывает.
 */

import {
  DOCUMENT_KIND_LABEL,
  DOCUMENT_KIND_SHORT,
  DOCUMENT_STATUS_LABEL,
  type DocumentDto,
  type DocumentKind,
  type DocumentStatus,
  type SellerProfileGap,
} from "../../../lib/api-crm";
import { formatRub } from "../../../lib/crm-deal-view";
import { formatShopDate } from "../../../lib/shop-time";
import type { ShopStatusMeta } from "../../../lib/shop/status-meta";

/**
 * Статусы документа (SC26, uiux-design §6): черновик — серый, выпущен (ждёт
 * подписи) — янтарный, подписан — зелёный, аннулирован — красный (как
 * «отменено», решение пользователя 2026-10-02).
 */
export const DOCUMENT_STATUS_META: Record<DocumentStatus, ShopStatusMeta> = {
  DRAFT: { label: DOCUMENT_STATUS_LABEL.DRAFT, tone: "neutral" },
  ISSUED: { label: DOCUMENT_STATUS_LABEL.ISSUED, tone: "attention" },
  SIGNED: { label: DOCUMENT_STATUS_LABEL.SIGNED, tone: "success" },
  VOIDED: { label: DOCUMENT_STATUS_LABEL.VOIDED, tone: "error" },
};

/**
 * Ссылка внутри строки или плитки сведений: цель касания по теме (48 до
 * 1024, 32 с 1024), текст цветом действия. Классом, чтобы основание и
 * контрагент в плитках выглядели одинаково.
 */
export const INLINE_LINK_CLASS =
  "inline-flex min-h-[var(--control-compact-h,3rem)] max-w-full items-center rounded-sm text-primary underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring";

/** Виды и статусы списком — в порядке реестров подписей (фильтры, формы). */
export const DOCUMENT_KINDS = Object.keys(DOCUMENT_KIND_LABEL) as DocumentKind[];
export const DOCUMENT_STATUSES = Object.keys(DOCUMENT_STATUS_LABEL) as DocumentStatus[];

/** Виды, которые создаются из карточки сделки (прежний набор вкладки). */
export const DEAL_DOCUMENT_KINDS: readonly DocumentKind[] = ["INVOICE", "COMMERCIAL_OFFER", "UPD_STATUS_2", "ACT", "CONTRACT"];

/**
 * Деньги документа: «1 175,00 ₽». Не рубли — «1 175,00 USD» (знак валюты
 * кабинет не угадывает). Пусто — «—».
 */
export function formatDocMoney(value: string | null | undefined, currency = "RUB"): string {
  if (currency.toUpperCase() === "RUB") return formatRub(value);
  const rub = formatRub(value);
  return rub === "—" ? rub : rub.replace("₽", currency.toUpperCase());
}

/** Дата документа: выпуска, а у черновика — создания («20.09.2026»). */
export function documentDate(doc: Pick<DocumentDto, "issuedAt" | "createdAt">): string {
  return formatShopDate(doc.issuedAt ?? doc.createdAt);
}

/**
 * Вид и номер для строки: «Счёт СЧТ-2026-00004»; без номера — «Счёт ·
 * черновик». Если номер уже начинается с названия вида («УПД-2026-…»,
 * «АКТ-2026-…»), вид не повторяем: «УПД УПД-2026-…» читалось как опечатка.
 */
export function documentTitle(doc: Pick<DocumentDto, "kind" | "number">): { kind: string | null; number: string | null } {
  const kind = DOCUMENT_KIND_SHORT[doc.kind];
  const number = doc.number || null;
  const repeated = number !== null && number.toLowerCase().startsWith(kind.toLowerCase());
  return { kind: repeated ? null : kind, number };
}

/**
 * Название страницы документа: номер, а у черновика — вид словами («Счёт на
 * оплату»). Что это черновик, говорит пилюля статуса рядом: «Черновик:
 * Коммерческое предложение» на 360 не помещалось и в две строки (QA-5 D7).
 */
export function documentPageTitle(doc: Pick<DocumentDto, "kind" | "number">): string {
  return doc.number || DOCUMENT_KIND_LABEL[doc.kind];
}

/** Позиция документа (строки печатной формы). */
export interface DocumentItemView {
  index: number;
  name: string;
  quantity: string;
  price: string;
  total: string;
}

/** Покупатель документа у любого статуса — сервер (`counterpartyName`); не определён — `null`. */
export function documentCounterparty(doc: Pick<DocumentDto, "counterpartyName">): string | null {
  return doc.counterpartyName?.trim() || null;
}

/** ИНН покупателя для сведений: «ИНН 7447000001»; у физлица — `null`. */
export function documentCounterpartyInn(doc: Pick<DocumentDto, "counterpartyInn">): string | null {
  return doc.counterpartyInn ? `ИНН ${doc.counterpartyInn}` : null;
}

/** Основание документа словами: «Заказ Z-2026-000023», «Сделка «Опт для кафе»». */
export function documentBasisText(doc: Pick<DocumentDto, "orderId" | "orderNumber" | "dealId" | "dealName">): string | null {
  if (doc.orderId) return doc.orderNumber ? `Заказ ${doc.orderNumber}` : "Заказ";
  if (doc.dealId) return doc.dealName ? `Сделка «${doc.dealName}»` : "Сделка";
  return null;
}

/**
 * Позиции документа — сервер (`items`, только в карточке): у выпущенного —
 * из снимка печати, у черновика — из заказа, как их напечатает выпуск.
 * Строки без названия пропускаем.
 */
export function documentItems(doc: Pick<DocumentDto, "items">): DocumentItemView[] {
  return (doc.items ?? []).flatMap((item) =>
    item.name.trim()
      ? [{ index: item.index, name: item.name.trim(), quantity: item.quantity || "—", price: item.price, total: item.total }]
      : [],
  );
}

/** Количество без хвостовых нулей: «2.000» → «2», «0.500» → «0,5». */
export function formatQuantity(value: string): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return value;
  return num.toLocaleString("ru-RU", { maximumFractionDigits: 3 });
}

/** Чего не хватает в реквизитах продавца — словами (реестр по коду сервера). */
export const LEGAL_PROFILE_GAP_LABEL: Record<SellerProfileGap, string> = {
  legalName: "название",
  inn: "ИНН",
  kpp: "КПП",
  ogrn: "ОГРН",
  legalAddress: "юридический адрес",
  bankAccount: "расчётный счёт",
  bik: "БИК",
  signer: "подписант",
};

/**
 * Пробелы в реквизитах словами: «Не указаны: ИНН, расчётный счёт». Порядок —
 * как в реестре подписей; повторы (несколько документов) схлопываются;
 * неизвестный код сервера — пропускаем; пусто — `null`.
 */
export function legalProfileGapsText(gaps: readonly string[]): string | null {
  const labels = (Object.keys(LEGAL_PROFILE_GAP_LABEL) as SellerProfileGap[])
    .filter((key) => gaps.includes(key))
    .map((key) => LEGAL_PROFILE_GAP_LABEL[key]);
  return labels.length > 0 ? `Не указаны: ${labels.join(", ")}` : null;
}
